import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ConfigOption, FormlyFieldConfig } from '@ngx-formly/core';

/**
 * Rejects common script / PHP injection payloads in free-text fields.
 * Uses a blocklist so normal names, addresses, and descriptions still pass.
 */
export const DANGEROUS_INPUT_PATTERN =
  /(<\s*\/?\s*script\b|<\?php|<\?=|<\?|\?>|javascript\s*:|vbscript\s*:|data\s*:\s*text\/html|on[a-z]+\s*=|<\s*\/?\s*(iframe|object|embed|applet|svg|meta|link|style|base)\b)/i;

export const NO_SCRIPT_INPUT_ERROR = 'noScriptInput';

export const NO_SCRIPT_INPUT_MESSAGE =
  'This field cannot contain script tags, PHP code, or HTML event handlers.';

const NON_TEXT_INPUT_TYPES = new Set([
  'number',
  'date',
  'datetime-local',
  'time',
  'month',
  'week',
  'checkbox',
  'radio',
  'file',
  'hidden',
  'color',
  'range',
  'submit',
  'button',
  'reset',
  'image',
]);

export function isTextLikeInputElement(
  element: HTMLInputElement | HTMLTextAreaElement
): boolean {
  if (element.tagName === 'TEXTAREA') {
    return true;
  }

  const type = (element.getAttribute('type') || element.type || 'text').toLowerCase();
  return !NON_TEXT_INPUT_TYPES.has(type);
}

export function isSafeTextInput(value: unknown): boolean {
  if (value == null || value === '') {
    return true;
  }

  if (typeof value !== 'string') {
    return true;
  }

  return !DANGEROUS_INPUT_PATTERN.test(value);
}

export function noScriptInputValidator(
  control: AbstractControl,
  _field?: FormlyFieldConfig
): ValidationErrors | null {
  return isSafeTextInput(control.value) ? null : { [NO_SCRIPT_INPUT_ERROR]: true };
}

export const noScriptInputAngularValidator: ValidatorFn = (control: AbstractControl) =>
  noScriptInputValidator(control);

/** Append no-script validation when calling setValidators (which replaces existing validators). */
export function withNoScriptValidators(
  validators?: ValidatorFn | ValidatorFn[] | null
): ValidatorFn[] {
  const list =
    validators == null ? [] : Array.isArray(validators) ? [...validators] : [validators];

  if (!list.includes(noScriptInputAngularValidator)) {
    list.push(noScriptInputAngularValidator);
  }

  return list;
}

const controlsWithNoScriptValidator = new WeakSet<AbstractControl>();

export function ensureNoScriptInputValidator(control: AbstractControl | null): void {
  if (!control || controlsWithNoScriptValidator.has(control)) {
    return;
  }

  controlsWithNoScriptValidator.add(control);

  const existing = control.validator;
  const composed = existing
    ? Validators.compose([existing, noScriptInputAngularValidator])
    : noScriptInputAngularValidator;

  control.setValidators(composed);
  control.updateValueAndValidity({ emitEvent: false });
}

export const noScriptInputFormlyConfig: ConfigOption = {
  validators: [
    {
      name: NO_SCRIPT_INPUT_ERROR,
      validation: noScriptInputValidator,
    },
  ],
  validationMessages: [
    {
      name: NO_SCRIPT_INPUT_ERROR,
      message: NO_SCRIPT_INPUT_MESSAGE,
    },
  ],
  types: [
    {
      name: 'input',
      defaultOptions: {
        validators: {
          validation: [NO_SCRIPT_INPUT_ERROR],
        },
      },
    },
    {
      name: 'textarea',
      defaultOptions: {
        validators: {
          validation: [NO_SCRIPT_INPUT_ERROR],
        },
      },
    },
  ],
};
